import { useEffect, useMemo, useState } from 'react'
import { useBlocker } from 'react-router-dom'

import { DragHandle } from '@tiptap/extension-drag-handle-react'
import { EditorContent, useEditor } from '@tiptap/react'
import { ref } from 'valtio'

import { getContentForRichtextCell } from '~shared/types/Cell'

import { CLIENT_RICHTEXT_EXTENSIONS } from '@/features/richtext/richtext.extensions'
import { TIPTAP_CLASSES } from '@/features/richtext/tiptap.helpers'
import { getCell, useCellSnapshot } from '@/features/rundown/data/cells.store'

import { saveKeys } from '../../../grid/grid.save'
import { getLastSeenUpdatedAt } from '../../../grid/grid.save.helpers'
import { getCellTextOptimistic } from '../../../grid/grid.save.hooks'
import {
  gridState,
  gridStateActions,
  useGridSnap,
} from '../../../grid/grid.store'
import { cellCommands, createSaveCellCommand } from '../../cell.commands'
import { useCellKey } from '../../cell.context'
import { CellClickOutsideController, useCellIsReadOnly } from '../../cell.hooks'
import { CellToolbar } from '../../toolbar/CellToolbar'
import { CellToolbarEditorItems } from '../../toolbar/CellToolbar.editoritems'
import { CellUpload } from '../../upload/CellUpload'

import { handlePendingPaste } from './tiptap.clipboard'
import { handleEditorFocus } from './tiptap.focus'

export function RichtextColumnCellEditor() {
  const cellKey = useCellKey()
  const cell = useCellSnapshot()

  const isReadOnly = useCellIsReadOnly()

  const cellContentAsHTML = getContentForRichtextCell(cell?.content)

  const [optimisticContent] = useState(() =>
    getCellTextOptimistic(cellKey, cellContentAsHTML),
  )

  const editor = useEditor({
    extensions: CLIENT_RICHTEXT_EXTENSIONS,
    content: optimisticContent,
    editable: !isReadOnly,
    editorProps: {
      attributes: {
        class:
          TIPTAP_CLASSES.LIVE +
          ' ' +
          // Outlines: default, focused, when group's drag-handle is active
          'outline-2 outline-neutral-500 focus:outline-blue-500 group-active:outline-blue-500',
        // a11y properties
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Cell content',
      },
    },
    onCreate({ editor }) {
      // Store closure so `getHTML()` can be accessed outside this component
      gridState.editor.getHTMLref = ref(() => editor.getHTML())

      // Run clipboard paste helper
      handlePendingPaste(editor)

      // Run focus helper
      handleEditorFocus(editor)
    },

    onUpdate({ editor }) {
      // TipTap does not track dirty state
      // So we calculate it manually and store in state.
      // Compare against the content the editor was seeded with, which can be
      // a confirmed save the listener has not delivered yet.
      const initialContent = JSON.stringify(optimisticContent)
      const currentContent = JSON.stringify(editor.getHTML())
      gridState.editor.isDirty = initialContent !== currentContent
    },

    onDestroy() {
      // Reset state
      gridStateActions.editor.resetEditor()
    },
  })

  const localCommands = useMemo(
    () => [
      cellCommands.discard,
      createSaveCellCommand({
        execute: () => gridStateActions.editor.finishEditing('explicit'),
      }),
    ],
    [],
  )

  return (
    <CellUpload>
      <EditorContent
        editor={editor}
        role="none" // a11y: purely a wrapper
        className="group relative w-full h-full rounded-sm isolate"
      />

      <DragHandle
        editor={editor}
        nested={true}
        onElementDragEnd={() => {
          // Focus editor after dragged handle is dropped
          editor.commands.focus(null, { scrollIntoView: false })
        }}
      >
        <span
          onClick={() => {
            // Focus editor when handle is clicked (not dragged)
            editor.commands.focus(null, { scrollIntoView: false })
          }}
        >
          ⠿
        </span>
      </DragHandle>

      <CellClickOutsideController
        handler={() => {
          gridStateActions.editor.finishEditing('blur')
        }}
      />

      <EditorUnsavedController />

      <EditorConflictController />

      <CellToolbar
        onKeyTab={() => {
          editor.commands.focus(null, { scrollIntoView: false })
        }}
        localCommands={localCommands}
      >
        <CellToolbarEditorItems editor={editor} />
      </CellToolbar>
    </CellUpload>
  )
}

/**
 * Headless controller component that watches the editor for unsaved changes,
 * blocks navigation and prompts the user to confirm.
 *
 * Handles both browser and react-router navigation.
 */
function EditorUnsavedController() {
  const {
    editor: { isDirty },
  } = useGridSnap()

  const blocker = useBlocker(isDirty)

  useEffect(() => {
    if (!isDirty) return

    // Handle react-router navigation
    if (blocker.state === 'blocked') {
      const confirmed = window.confirm(
        'Your changes have not been saved, do you wish to leave the page?',
      )
      if (confirmed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
      return
    }

    // Handle browser navigation
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = '' // Required by some browsers to trigger dialog
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [blocker, isDirty])

  return null
}

/**
 * Checks if a cell has become stale while the user is editing it.
 * Meaning the cells content was updated on the server by someone else.
 */
function EditorConflictController() {
  const cellKey = useCellKey()
  const cell = useCellSnapshot()

  const cellUpdatedAt = cell?.updatedAt

  // Version the editor content is based on, fixed at mount. Includes our own
  // confirmed save, so its late snapshot does not count as a conflict.
  const [baselineUpdatedAt] = useState(() =>
    getLastSeenUpdatedAt(
      getCell(cellKey)?.updatedAt,
      gridState.saveRegistry[saveKeys.cell(cellKey)],
    ),
  )

  // useEffect: flags a conflict when the listener delivers a newer version
  // mid-session. Writing to the store during render is not allowed.
  useEffect(() => {
    if (!cellUpdatedAt || !baselineUpdatedAt) return

    if (cellUpdatedAt > baselineUpdatedAt) {
      gridState.editor.isConflicting = true
    }
  }, [baselineUpdatedAt, cellUpdatedAt])

  return null
}
